import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, Package, Store, AlertCircle, Tag, Tags, Sparkles, Warehouse } from "lucide-react";
import { api } from "../lib/api";
import type { ChannelSummary, HomeSummary } from "../lib/types";
import { PageHeader } from "../components/layout/PageHeader";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { SectionHeading } from "../components/ui/section-heading";
import { Skeleton } from "../components/ui/skeleton";
import { EmptyState } from "../components/ui/empty-state";
import { Badge } from "../components/ui/badge";
import { relativeTime } from "../lib/utils";
import { channelTitle, channelSubtitle } from "../lib/channel-labels";
import { useAuth } from "../contexts/AuthContext";
import { FilaDeTrabalho } from "./FilaDeTrabalho";

interface Atalho {
  to: string;
  label: string;
  meta: string;
  icon: JSX.Element;
  show: boolean;
}

export function HomePage(): JSX.Element {
  const { user, can } = useAuth();
  const { data: channels = [], isLoading, isError, error } = useQuery<ChannelSummary[]>({
    queryKey: ["channels"],
    queryFn: async () => {
      const r = await api.get<ChannelSummary[]>("/api/channels");
      return r.data;
    },
  });

  // Contagem real dos cadastros (brief fidelidade-3: "Painel ... Cadastros
  // mostram contagem real no meta"). Ausente em API antiga -> undefined, e
  // cada atalho cai no proprio texto generico de fallback (nunca quebra,
  // nunca inventa numero — mesma regra dos facets do Canal).
  const { data: summary } = useQuery<HomeSummary>({
    queryKey: ["home-summary"],
    queryFn: async () => {
      const r = await api.get<HomeSummary>("/api/home/summary");
      return r.data;
    },
    retry: false,
  });

  const atalhos: Atalho[] = [
    {
      to: "/admin/categorias",
      label: "Categorias",
      meta: summary?.categorias ?? "Hierarquia de 2 níveis",
      icon: <Tag className="icon" aria-hidden />,
      show: can("categories.write"),
    },
    {
      to: "/admin/tags",
      label: "Tags",
      meta: summary?.tags ?? "Etiquetas de produto",
      icon: <Tags className="icon" aria-hidden />,
      show: can("tags.write"),
    },
    {
      to: "/admin/tecnicidades",
      label: "Tecnicidades",
      meta: summary?.tecnicidades ?? "Selos técnicos do produto",
      icon: <Sparkles className="icon" aria-hidden />,
      show: can("technicalities.write"),
    },
    {
      to: "/admin/estoques",
      label: "Estoques",
      meta: summary?.estoques ?? "Depósitos e origem",
      icon: <Warehouse className="icon" aria-hidden />,
      show: can("stock.read"),
    },
  ].filter((a) => a.show);

  return (
    <>
      {/* mobileTitle="Início" (handoff: mobileTitle:'Início' do Painel) —
          achado do smoke comparativo 2026-09-22: sem isto, a barra coral do
          mobile repetia o MESMO texto "Olá, <nome>" que o H1 do conteudo ja
          mostra logo abaixo, so que cortado pelo espaco da barra. Duas
          fontes de saudacao, zero informacao nova. */}
      <PageHeader
        title={`Olá, ${user?.name?.split(" ")[0] ?? ""}`}
        description="O que está pendente hoje, seus canais e os cadastros do catálogo. Use a busca do topo para achar um SKU em qualquer canal."
        mobileTitle="Início"
      />

      {/* Grade 12 colunas unica — canal (8) + primeira-vez (4), cadastros
          (6+6) e fila (12) sao TODOS irmãos desta mesma grade, com o mesmo
          gap .75rem (handoff): nada de sub-grades com gap proprio, que e
          o que produzia espaçamento desigual entre blocos irmãos. */}
      <div className="grid-12">
        <section aria-labelledby="canais-heading" className="gc-12 grid-12">
          <h2 id="canais-heading" className="sr-only">
            Canais disponíveis
          </h2>

          {isLoading ? (
            <>
              {[0, 1].map((i) => (
                <div className="gc-6" key={i}>
                  <Card>
                    <CardHeader>
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-48 mt-2" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-12 w-full" />
                    </CardContent>
                  </Card>
                </div>
              ))}
            </>
          ) : isError ? (
            <div className="gc-12">
              <EmptyState
                icon={<AlertCircle className="h-12 w-12" />}
                title="Erro ao carregar canais"
                description={(error as Error)?.message ?? "Tente recarregar a página"}
              />
            </div>
          ) : channels.length === 0 ? (
            <div className="gc-12">
              <EmptyState
                icon={<Store className="h-12 w-12" />}
                title="Nenhum canal disponível"
                description="Você ainda não tem acesso a nenhum canal. Peça para um admin liberar."
              />
            </div>
          ) : (
            <>
              {channels.map((channel) => (
                <div className="gc-8" key={channel.slug}>
                  <ChannelCard channel={channel} />
                </div>
              ))}
              <div className="gc-4">
                <Link to="/manual" className="block h-full" style={{ textDecoration: "none", color: "inherit" }}>
                  <Card accentLeft="lilac" className="h-full stack">
                    <div className="spread">
                      <span style={{ fontWeight: "var(--fw-black)", fontSize: "var(--fs-xl)" }}>
                        Primeira vez aqui?
                      </span>
                      <span style={{ color: "var(--lilac)", fontWeight: "var(--fw-black)" }} aria-hidden>
                        →
                      </span>
                    </div>
                    <p className="muted" style={{ margin: 0, fontSize: "var(--fs-base)", lineHeight: 1.5 }}>
                      Manual do operador: entrar, achar produto, colocar fotos, publicar.
                    </p>
                  </Card>
                </Link>
              </div>
            </>
          )}
        </section>

        {atalhos.length > 0 ? (
          <section aria-labelledby="cadastros-heading" className="gc-12 grid-12">
            <div className="gc-12">
              <SectionHeading id="cadastros-heading" dot="lilac" title="Cadastros do catálogo" />
            </div>
            {atalhos.map((a) => (
              <div className="gc-6" key={a.to}>
                <Link to={a.to} style={{ textDecoration: "none", color: "inherit" }}>
                  <Card className="cluster">
                    <span
                      aria-hidden
                      style={{
                        width: "2.25rem",
                        height: "2.25rem",
                        borderRadius: "var(--r-md)",
                        background: "var(--lilac-soft)",
                        color: "var(--lilac)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {a.icon}
                    </span>
                    <span className="grow">
                      <span style={{ display: "block", fontWeight: "var(--fw-black)" }}>{a.label}</span>
                      <span className="muted" style={{ display: "block", fontSize: "var(--fs-sm)" }}>
                        {a.meta}
                      </span>
                    </span>
                    <span className="muted" aria-hidden>
                      →
                    </span>
                  </Card>
                </Link>
              </div>
            ))}
          </section>
        ) : null}

        <FilaDeTrabalho canais={channels} />
      </div>
    </>
  );
}

function ChannelCard({ channel }: { channel: ChannelSummary }): JSX.Element {
  const stats = channel.stats ?? { total: 0, pending: 0, last_activity_at: null };
  return (
    <Link to={`/canais/${channel.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
      <Card accentLeft="aqua" className="h-full">
        <div className="spread">
          <div className="min-w-0">
            <div className="card__title truncate">{channelTitle(channel)}</div>
            <div className="card__meta truncate">{channelSubtitle(channel)}</div>
          </div>
          <span style={{ color: "var(--aqua)", fontWeight: "var(--fw-black)" }} aria-hidden>
            →
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0,1fr))",
            gap: "var(--sp-3)",
            marginTop: "var(--sp-4)",
          }}
        >
          <Stat
            label="Total"
            fullLabel={stats.archived ? `Total (fora ${stats.archived} arquivado${stats.archived === 1 ? "" : "s"})` : undefined}
            value={stats.total}
            icon={<Package className="icon" style={{ width: "0.75rem", height: "0.75rem" }} />}
          />
          <Stat
            label="Pendentes"
            value={stats.pending}
            icon={<AlertCircle className="icon" style={{ width: "0.75rem", height: "0.75rem" }} />}
            accent={stats.pending > 0}
          />
          <Stat
            label="Atividade"
            fullLabel="Última atividade"
            value={relativeTime(stats.last_activity_at)}
            icon={<Clock className="icon" style={{ width: "0.75rem", height: "0.75rem" }} />}
            raw
          />
        </div>
        {!channel.active ? (
          <div style={{ marginTop: "var(--sp-3)" }}>
            <Badge variant="warning">Inativo</Badge>
          </div>
        ) : null}
      </Card>
    </Link>
  );
}

interface StatProps {
  label: string;
  /** Rotulo completo, so no desktop (>=40rem) — no mobile o rotulo curto
   * evita truncar "ÚLTIMA ATIVID…" no grid de 3 colunas a 390px (smoke
   * comparativo 2026-09-22, familia 5: prototipo mobile usa "ATIVIDADE"). */
  fullLabel?: string;
  value: number | string;
  icon: React.ReactNode;
  accent?: boolean;
  raw?: boolean;
}
function Stat({ label, fullLabel, value, icon, accent, raw }: StatProps): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="cluster" style={{ fontSize: "var(--fs-micro)", fontWeight: "var(--fw-bold)", textTransform: "uppercase", letterSpacing: ".05em", color: "var(--ink-3)", gap: ".25rem" }}>
        <span aria-hidden>{icon}</span>
        {fullLabel ? (
          <>
            <span className="truncate sm:hidden">{label}</span>
            <span className="truncate hidden sm:inline">{fullLabel}</span>
          </>
        ) : (
          <span className="truncate">{label}</span>
        )}
      </div>
      <div
        style={{
          marginTop: ".125rem",
          fontWeight: "var(--fw-black)",
          fontVariantNumeric: "tabular-nums",
          fontSize: raw ? "var(--fs-base)" : "var(--fs-2xl)",
          color: accent ? "var(--warn)" : "var(--ink)",
        }}
        className="truncate"
      >
        {value}
      </div>
    </div>
  );
}
